import { FC, ReactNode, useContext, useEffect, useRef, useState, isValidElement } from "react";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { Button, Dialog } from "@headlessui/react";
import Card from "../../card";
import screenFullLoading from "@/core/utils/loading";
import { GlobalComponentsContext } from "@/core/contexts";
import { CircleLoader, PuffLoader } from "react-spinners";

const WithGlobalComponents: FC<{
    children: ReactNode
}> = ({ children }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [dialog, setDialog] = useState<boolean>(false);
    const [dialogProps, setDialogProps] = useState<DialogComponentProps<ReactNode | DialogDefaultProps>>();
    const value: GlobalComponentsContextValue = {
        loading: {
            showLoading: () => {
                setLoading(true);
                return Promise.resolve(true);
            },
            hideLoading: () => {
                setLoading(false);
                return Promise.resolve(false);
            }
        },
        dialog: {
            showDialog<T = DialogDefaultProps>(child: T, close = true) {
                setDialogProps({
                    child: child as any,
                    close,
                });
                return Promise.resolve(true);
            },
            hideDialog: () => {
                setDialog(false);
                return Promise.resolve(false);
            }
        }
    };

    screenFullLoading.subScribe(() => {
        setLoading(screenFullLoading.isLoading);
    });

    useEffect(() => {
        if (!dialog) setDialog(true);
        //不允许监听 dialog
    }, [dialogProps]);

    return <GlobalComponentsContext.Provider value={value}>
        <div id="_notification-root">
            <ToastContainer
                position="bottom-center"
                autoClose={2000}
            />
            <div>
                <CustomLoading loading={loading} />
                <CustomDialog dialog={dialog} dialogProps={dialogProps} />
                {children}
            </div>
        </div>

    </GlobalComponentsContext.Provider>
}


const CustomDialog: FC<{ dialog: boolean, dialogProps: Nullable<DialogComponentProps<ReactNode | DialogDefaultProps>> }> = ({
    dialog,
    dialogProps,
}) => {
    const context = useContext(GlobalComponentsContext);

    if (!dialogProps) return <></>

    const { child, close } = dialogProps;

    const handleClose = () => {
        if (close) context?.dialog.hideDialog();
    }


    if (isValidElement(child)) {
        return <Dialog open={dialog} onClose={handleClose} >
            {child}
        </Dialog>
    }
    const content: DialogDefaultProps = child as DialogDefaultProps;
    const { cancel = true, confirm = true } = content;
    return <Dialog open={dialog} onClose={handleClose} className="z-[99999999] w-screen h-screen fixed top-0 left-0 bg-[rgba(0,0,0,.54)] flex items-center justify-center animate-in fade-in animate-ou face-out">
        <div className="min-w-96  max-sm:min-w-full">
            <Card>
                <div className="p-4">
                    <div className="w-full text-start pb-2">
                        <h6 className="font-semibold">
                            {content.title}
                        </h6>
                    </div>
                    <p>
                        {content.content}
                    </p>
                    {
                        content.contentNode
                    }
                    <div className="flex mt-2 justify-between gap-2">
                        {
                            cancel && <Button onClick={
                                () => {
                                    content?.onCancel?.();
                                    handleClose();
                                }
                            } className="flex-1 rounded-md border border-border" >{
                                    <p>
                                        {content.cancelText ?? "取消"}
                                    </p>
                                }</Button>
                        }
                        {confirm && <Button
                            onClick={
                                () => {
                                    content?.onConfirm?.();
                                    handleClose();
                                }
                            }
                            className="flex-1 py-2 bg-primary text-center rounded-md" >
                            <div className="text-dark-text ">
                                {
                                    content.confirmText ?? "确定"
                                }
                            </div>
                        </Button>}
                    </div>
                </div>
            </Card>
        </div>
    </Dialog>

}


const CustomLoading: FC<{ loading: boolean }> = ({ loading }) => {
    const context = useContext(GlobalComponentsContext);
    const loadingTimer = useRef<any>(0);
    const timeout = Number(process.env.REACT_APP_LOADING_TIMEOUT_SECOND ?? 10);
    useEffect(() => {
        if (loading) {
            loadingTimer.current = setTimeout(() => {
                context!.loading.hideLoading();
            }, 1000 * timeout)
        } else {
            if (loadingTimer.current) clearTimeout(loadingTimer.current);
        }
    }, [context, loading, timeout]);
    return <div className="w-screen h-screen fixed top-0 left-0 z-[99999999] " style={{
        opacity: loading ? 1 : 0,
        left: loading ? "0px" : "-200%",
        transition: "opacity .15s ease-in"
    }}>
        <div className="w-full h-full flex items-center justify-center">
            <div className="w-20 h-20 animate-in fade-in">
                {/* <img src={`${process.env.PUBLIC_URL}/assets/images/loading.gif`} alt="loading" /> */}
                {/* <CircleLoader color="#00be46"/> */}
                <PuffLoader color="#00be46" />
            </div>
        </div>
    </div>;
}
export default WithGlobalComponents;